import React from "react";
import { childrenWithProps, classNames } from "../../tools/react.dom.helpers";
import Pane from "./pane";
import { UrlTabsHelper } from "../../tools/urlTabsHelper";

// MultiPane: A tab container component that can sync with URL and support controlled/uncontrolled modes
export default class MultiPane extends React.Component {
    childType = Pane; // Only children of type Pane are considered as tab panes

    constructor(props) {
        super(props);

        if (props.syncWithUrl) {
            UrlTabsHelper.init(); // Initialize URL tab helper if syncing with URL
        }

        this.state = {
            selectedPane: this._getInitialPane(), // Set initial selected pane
        };
    }

    // Determines the initial selected pane
    _getInitialPane() {
        const { controlled, value, children, syncWithUrl } = this.props;
        // If syncWithUrl={true} – tabs are synced with URL and respond to Back/Forward. If not set – work locally without changing history.
        // <MultiPane syncWithUrl={true}> ...

        if (controlled && value) return value; // Controlled mode: use value prop
        if (syncWithUrl) {
            const paneFromUrl = UrlTabsHelper.getTabFromUrl();
            if (paneFromUrl) return paneFromUrl; // If tab is in URL, use it
        }

        let defaultPane = null;
        React.Children.forEach(children, (child) => {
            if (child.type === this.childType) {
                // Select the first child with selected or visible prop as default
                if (!defaultPane && (child.props.selected || child.props.visible)) {
                    defaultPane = child.props.id;
                }
            }
        });

        return defaultPane;
    }

    componentDidMount() {
        const { syncWithUrl, controlled, onChange } = this.props;

        if (syncWithUrl) {
            // Subscribe to URL tab changes
            this.unsubscribe = UrlTabsHelper.subscribe((tab) => {
                if (!controlled) {
                    this.setState({ selectedPane: tab }); // Update state if uncontrolled
                }
                onChange?.(tab); // Call onChange callback if provided
            });
        }
    }

    componentWillUnmount() {
        this.unsubscribe?.(); // Unsubscribe from URL tab changes
    }

    // Sync state with props in controlled mode
    static getDerivedStateFromProps(nextProps, prevState) {
        if (nextProps.controlled && nextProps.value !== prevState.selectedPane) {
            return { selectedPane: nextProps.value };
        }
        return null;
    }

    // Select a pane by id
    selectPane(id) {
        const { controlled, onChange, syncWithUrl } = this.props;

        if (id === this.state.selectedPane) return; // Prevent duplicate selection

        if (!controlled) {
            this.setState({ selectedPane: id }); // Update state if uncontrolled
        }

        if (syncWithUrl) UrlTabsHelper.setTabInUrl(id); // Update URL if syncing
        onChange?.(id); // Call onChange callback if provided
    }

    render() {
        const { controlled, value, children, className } = this.props;
        const selectedPane = controlled ? value : this.state.selectedPane;

        return (
            <div className={classNames("tab-container", className)}>
                {childrenWithProps(children, (child) => {
                    let result = {};

                    if (child.type === this.childType) {
                        // Show only the selected pane
                        result.visible = selectedPane
                            ? selectedPane === child.props.id
                            : child.props.visible;
                    } else {
                        // Pass value and onChange to non-pane children (e.g., tab headers)
                        result.value = selectedPane || child.props.value;
                        result.onChange = (id) => this.selectPane(id);
                    }

                    return result;
                })}
            </div>
        );
    }
}
